import { useQueryClient } from 'react-query';
import { Asset, QUERY_KEYS } from '@24i/nxg-sdk-photon';
import { useState } from 'react';
import { log } from '@24i/nxg-core-utils/src/logger';

import { handleError } from '../../../utils/error';
import { useContentData } from '../../../context/ContentData';
import { useAppConfigQuery } from '../useAppConfigQuery';
import { FetchAssetRelatedResult, FetchAssetRelatedParams } from './types';

export const useAssetRelatedFetch = (): FetchAssetRelatedResult<Asset[]> => {
    const queryClient = useQueryClient();
    const { fetchRecommended } = useContentData();
    const { appSettings } = useAppConfigQuery();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | undefined>(undefined);

    const pageSize = appSettings?.features?.recommendedRow?.pageSize || 50;

    const fetch = async (params?: FetchAssetRelatedParams): Promise<Asset[]> => {
        try {
            setIsLoading(true);
            setError(undefined);

            if (params?.forceRefresh) {
                await queryClient.invalidateQueries({
                    queryKey: [
                        QUERY_KEYS.assets,
                        QUERY_KEYS.assetRelated,
                        params?.assetId,
                        params?.assetType,
                    ],
                });
            }

            return await queryClient.fetchQuery({
                queryKey: [
                    QUERY_KEYS.assets,
                    QUERY_KEYS.assetRelated,
                    params?.assetId,
                    params?.assetType,
                ],
                queryFn: () => fetchRecommended(params?.assetId, params?.assetType, pageSize),
            });
        } catch (e) {
            const err = handleError(e, 'Failed to fetch asset related data');
            setError(err);
            log('Error fetching asset related: ', err);
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    return { fetch, isLoading, error };
};
